import React, { useState } from 'react';
import styled from 'styled-components';
import yaml from 'js-yaml';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.7);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Modal = styled.div`
  background: ${props => props.$darkMode ? '#161b22' : '#fff'};
  border-radius: 12px;
  width: 600px;
  max-height: 80vh;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0,0,0,0.5);
  border: 1px solid ${props => props.$darkMode ? '#30363d' : '#d0d7de'};
`;

const Header = styled.div`
  padding: 24px;
  border-bottom: 1px solid ${props => props.$darkMode ? '#30363d' : '#d0d7de'};
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: ${props => props.$darkMode ? '#0d1117' : '#f6f8fa'};
`;

const Title = styled.h2`
  margin: 0;
  color: ${props => props.$darkMode ? '#e6edf3' : '#24292f'};
  font-size: 20px;
`;

const CloseBtn = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: ${props => props.$darkMode ? '#8b949e' : '#57606a'};
`;

const Content = styled.div`
  padding: 24px;
  overflow-y: auto;
  max-height: calc(80vh - 140px);
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: ${props => props.$darkMode ? '#e6edf3' : '#24292f'};
  font-size: 14px;
`;

const Select = styled.select`
  width: 100%;
  padding: 10px;
  border: 1px solid ${props => props.$darkMode ? '#30363d' : '#d0d7de'};
  border-radius: 6px;
  background: ${props => props.$darkMode ? '#0d1117' : '#fff'};
  color: ${props => props.$darkMode ? '#e6edf3' : '#24292f'};
  font-size: 14px;
  margin-bottom: 20px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 12px;
  margin-bottom: 20px;
`;

const LangCard = styled.div`
  padding: 16px;
  border: 2px solid ${props => props.$selected ? '#0969da' : (props.$darkMode ? '#30363d' : '#d0d7de')};
  border-radius: 8px;
  cursor: pointer;
  text-align: center;
  background: ${props => props.$selected ? (props.$darkMode ? '#0d1117' : '#ddf4ff') : 'transparent'};
  transition: all 0.2s;
  
  &:hover {
    border-color: #0969da;
  }
`;

const Footer = styled.div`
  padding: 16px 24px;
  border-top: 1px solid ${props => props.$darkMode ? '#30363d' : '#d0d7de'};
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  background: ${props => props.$darkMode ? '#0d1117' : '#f6f8fa'};
`;

const Button = styled.button`
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  font-size: 14px;
  
  ${props => props.$primary ? `
    background: #0969da;
    color: white;
    &:hover { background: #0860ca; }
    &:disabled { background: #6e7781; cursor: not-allowed; }
  ` : `
    background: ${props.$darkMode ? '#21262d' : '#f6f8fa'};
    color: ${props.$darkMode ? '#e6edf3' : '#24292f'};
    border: 1px solid ${props.$darkMode ? '#30363d' : '#d0d7de'};
    &:hover { background: ${props.$darkMode ? '#30363d' : '#eaeef2'}; }
  `}
`;

const languages = [
  { id: 'typescript-fetch', name: 'TypeScript', icon: '📘' },
  { id: 'javascript', name: 'JavaScript', icon: '📙' },
  { id: 'python', name: 'Python', icon: '🐍' },
  { id: 'java', name: 'Java', icon: '☕' },
  { id: 'go', name: 'Go', icon: '🔷' },
  { id: 'ruby', name: 'Ruby', icon: '💎' },
  { id: 'php', name: 'PHP', icon: '🐘' },
  { id: 'csharp', name: 'C#', icon: '🔷' },
  { id: 'swift', name: 'Swift', icon: '🍎' },
  { id: 'kotlin', name: 'Kotlin', icon: '🟣' },
];

function SdkGeneratorModal({ spec, darkMode, onClose }) {
  const [selected, setSelected] = useState('typescript-fetch');
  const [generating, setGenerating] = useState(false);

  const generate = async () => {
    setGenerating(true);
    try {
      const specObj = typeof spec === 'string' ? yaml.load(spec) : spec;
      
      // Try OpenAPI Generator API
      const response = await fetch('https://api.openapi-generator.tech/api/gen/clients/' + selected, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          openAPIUrl: '',
          spec: specObj,
          options: {}
        })
      });

      if (!response.ok) {
        const error = await response.text();
        throw new Error(error || 'Generation failed');
      }
      
      const result = await response.json();
      
      if (result.link) {
        // Download the generated SDK
        const downloadUrl = `https://api.openapi-generator.tech/api/gen/download/${result.code}`;
        window.open(downloadUrl, '_blank');
        onClose();
      } else {
        throw new Error('No download link received');
      }
    } catch (error) {
      alert('Failed to generate SDK: ' + error.message + '\n\nNote: SDK generation requires a valid OpenAPI 3.0 specification.');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Overlay onClick={onClose}>
      <Modal $darkMode={darkMode} onClick={e => e.stopPropagation()}>
        <Header $darkMode={darkMode}>
          <Title $darkMode={darkMode}>Generate SDK</Title>
          <CloseBtn $darkMode={darkMode} onClick={onClose}>&times;</CloseBtn>
        </Header>
        
        <Content>
          <Label $darkMode={darkMode}>Select Language/Framework</Label>
          <Grid>
            {languages.map(lang => (
              <LangCard
                key={lang.id}
                $darkMode={darkMode}
                $selected={selected === lang.id}
                onClick={() => setSelected(lang.id)}
              >
                <div style={{ fontSize: '32px', marginBottom: '8px' }}>{lang.icon}</div>
                <div style={{ fontSize: '13px', fontWeight: '600' }}>{lang.name}</div>
              </LangCard>
            ))}
          </Grid>
        </Content>

        <Footer $darkMode={darkMode}>
          <Button $darkMode={darkMode} onClick={onClose}>Cancel</Button>
          <Button $primary disabled={generating} onClick={generate}>
            {generating ? 'Generating...' : 'Generate SDK'}
          </Button>
        </Footer>
      </Modal>
    </Overlay>
  );
}

export default SdkGeneratorModal;
